import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Spinner } from "./components/ui-kit";

import Login from "./pages/Login";
import Landing from "./pages/Landing";
import Dashboard from "./pages/Dashboard";
import Intelligence from "./pages/Intelligence";
import AIResearch from "./pages/AIResearch";
import { News, Calendar } from "./pages/NewsCalendar";
import Leads from "./pages/Leads";
import Brokers from "./pages/Brokers";
import Clients from "./pages/Clients";
import Analytics from "./pages/Analytics";
import Settings from "./pages/Settings";
import { Journal, Telegram, Content, Team, Audit } from "./pages/MiscPages";
import ClientPortal from "./pages/ClientPortal";
import LiveResults from "./pages/LiveResults";
import VipTrades from "./pages/VipTrades";
import SendSignal from "./pages/SendSignal";
import FollowUp from "./pages/FollowUp";
import BrokerVault from "./pages/BrokerVault";
import Assistant from "./pages/Assistant";
import WhatsApp from "./pages/WhatsApp";
import Partners from "./pages/Partners";
import Workforce from "./pages/Workforce";
import TeamPortal from "./pages/TeamPortal";
import Join from "./pages/Join";
import Tutorial from "./pages/Tutorial";
import SignalAlert from "./pages/SignalAlert";
import ContentHub from "./pages/ContentHub";
import BlogPage from "./pages/BlogPage";
import GetApp from "./pages/GetApp";
import VipServices from "./pages/VipServices";
import { InstallApp } from "./components/InstallApp";
import { PushPrompt } from "./components/PushPrompt";

const homeFor = (role) => (role === "client" ? "/portal" : role === "team_member" ? "/team-portal" : "/dashboard");

function Loading() {
  return <div className="min-h-screen flex items-center justify-center" style={{ background: "var(--app-bg)" }}><Spinner label="Loading Fateh FX" /></div>;
}

function AdminRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "client" || user.role === "team_member") return <Navigate to={homeFor(user.role)} replace />;
  return children;
}

function ClientRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "client") return <Navigate to={homeFor(user.role)} replace />;
  return children;
}

function TeamRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "team_member") return <Navigate to={homeFor(user.role)} replace />;
  return children;
}

function Home() {
  const { user, loading } = useAuth();
  if (loading) return <Loading />;
  if (!user) return <Landing />;
  return <Navigate to={homeFor(user.role)} replace />;
}

const admin = (el) => <AdminRoute>{el}</AdminRoute>;

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster theme="dark" position="top-right" toastOptions={{ style: { background: "#111116", border: "1px solid #262630", color: "#F8FAFC", borderRadius: 0 } }} />
        <InstallApp />
        <PushPrompt />
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/login" element={<Login />} />
          <Route path="/open-account" element={<Landing />} />
          <Route path="/join" element={<Join />} />
          <Route path="/tutorial" element={<Tutorial />} />
          <Route path="/signal/:src/:sid" element={<SignalAlert />} />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/app" element={<GetApp />} />
          <Route path="/vip" element={<VipServices />} />
          <Route path="/blog/:slug" element={<BlogPage />} />
          <Route path="/results" element={<LiveResults />} />

          <Route path="/dashboard" element={admin(<Dashboard />)} />
          <Route path="/gold" element={admin(<Intelligence instrument="XAUUSD" />)} />
          <Route path="/btc" element={admin(<Intelligence instrument="BTCUSD" />)} />
          <Route path="/signals" element={<Navigate to="/vip-trades" replace />} />
          <Route path="/scalp" element={<Navigate to="/vip-trades" replace />} />
          <Route path="/ai" element={admin(<AIResearch />)} />
          <Route path="/news" element={admin(<News />)} />
          <Route path="/calendar" element={admin(<Calendar />)} />
          <Route path="/performance" element={<Navigate to="/vip-trades" replace />} />
          <Route path="/vip-trades" element={admin(<VipTrades />)} />
          <Route path="/send-signal" element={admin(<SendSignal />)} />
          <Route path="/follow-up" element={admin(<FollowUp />)} />
          <Route path="/broker-vault" element={admin(<BrokerVault />)} />
          <Route path="/assistant" element={admin(<Assistant />)} />
          <Route path="/whatsapp" element={admin(<WhatsApp />)} />
          <Route path="/journal" element={admin(<Journal />)} />
          <Route path="/clients" element={admin(<Clients />)} />
          <Route path="/crm" element={admin(<Leads />)} />
          <Route path="/brokers" element={admin(<Brokers />)} />
          <Route path="/telegram" element={admin(<Telegram />)} />
          <Route path="/content" element={admin(<Content />)} />
          <Route path="/automations" element={<Navigate to="/dashboard" replace />} />
          <Route path="/content-hub" element={admin(<ContentHub />)} />
          <Route path="/analytics" element={admin(<Analytics />)} />
          <Route path="/team" element={admin(<Team />)} />
          <Route path="/audit" element={admin(<Audit />)} />
          <Route path="/settings" element={admin(<Settings />)} />

          <Route path="/partners" element={admin(<Partners />)} />
          <Route path="/workforce" element={admin(<Workforce />)} />
          <Route path="/portal" element={<ClientRoute><ClientPortal /></ClientRoute>} />
          <Route path="/team-portal" element={<TeamRoute><TeamPortal /></TeamRoute>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}
